import type { FontInfo } from '@/types';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

export const useFontStore = defineStore('font', () => {
  /** 简中在线字体 */
  const appFonts = ref<Map<string, FontInfo>>(new Map());
  /** 简中在线字体 */
  const cnOnlineFonts = ref<Map<string, FontInfo>>(new Map());
  /** 繁中在线字体 */
  const zhcnOnlineFonts = ref<Map<string, FontInfo>>(new Map());
  /** 英文在线字体 */
  const enOnlineFonts = ref<Map<string, FontInfo>>(new Map());
  // 用户自定义字体列表
  const customFonts = ref<Map<string, FontInfo>>(new Map());
  // 常用字体列表
  const commonFonts = ref<Map<string, FontInfo>>(new Map());
  // 记录所有加到 document 的字体 key
  const registerFonts = ref<Map<string, FontInfo>>(new Map());

  const allFonts = computed(() => {
    const fonts = new Map<string, FontInfo>();
    appFonts.value.forEach((font) => fonts.set(font.fontFamily, font));
    registerFonts.value.forEach((font) => fonts.set(font.fontFamily, font));
    cnOnlineFonts.value.forEach((font) => fonts.set(font.fontFamily, font));
    zhcnOnlineFonts.value.forEach((font) => fonts.set(font.fontFamily, font));
    enOnlineFonts.value.forEach((font) => fonts.set(font.fontFamily, font));
    return fonts;
  });

  /**
   * 系统之外的字体
   */
  const extFonts = computed(() => {
    const fonts = new Map<string, FontInfo>();
    customFonts.value.forEach((font) => fonts.set(font.fontFamily, font));
    return fonts;
  });

  const allFontList = computed(() => Array.from(allFonts.value.values()));

  /** 简中在线字体列表 */
  const onlineFontList = computed(() => {
    const fonts = new Map<string, FontInfo>();
    cnOnlineFonts.value.forEach((font) => fonts.set(font.fontFamily, { ...font }));
    return Array.from(fonts.values());
  });

  /** 繁中在线字体列表 */
  const zhcnOnlineFontList = computed(() => {
    const fonts = new Map<string, FontInfo>();
    zhcnOnlineFonts.value.forEach((font) => fonts.set(font.fontFamily, { ...font }));
    return Array.from(fonts.values());
  });

  /** 英文在线字体列表 */
  const enOnlineFontList = computed(() => {
    const fonts = new Map<string, FontInfo>();
    enOnlineFonts.value.forEach((font) => fonts.set(font.fontFamily, { ...font }));
    return Array.from(fonts.values());
  });

  const getFontLabel = (fontFamily: string) => {
    if (allFonts.value.has(fontFamily)) {
      return allFonts.value.get(fontFamily)?.label || '';
    } else {
      return '';
    }
  };

  const getFontInfo = (fontFamily: string) => {
    if (allFonts.value.has(fontFamily)) {
      return allFonts.value.get(fontFamily);
    } else {
      return null;
    }
  };

  const getFontInfoByLabel = (label: string) => {
    const font = allFontList.value.find((font) => font.label === label);
    return font;
  };

  return {
    appFonts,
    cnOnlineFonts,
    zhcnOnlineFonts,
    enOnlineFonts,
    customFonts,
    commonFonts,
    registerFonts,
    allFontList,
    onlineFontList,
    zhcnOnlineFontList,
    enOnlineFontList,
    allFonts,
    extFonts,
    getFontLabel,
    getFontInfo,
    getFontInfoByLabel
  };
});
